// Renders model-written Markdown (chat answers) safely: react-markdown never
// renders raw HTML, links open in a new tab, and GitHub-flavoured extras
// (tables, task lists, strikethrough) come from remark-gfm. Styled with the
// app's own tokens rather than a typography plugin, so it sits in a chat
// bubble at body size.

import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'

const components: Components = {
  p: ({ children }) => <p className="my-1.5 first:mt-0 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="my-1.5 list-disc space-y-0.5 pl-5">{children}</ul>,
  ol: ({ children }) => <ol className="my-1.5 list-decimal space-y-0.5 pl-5">{children}</ol>,
  li: ({ children }) => <li className="pl-0.5">{children}</li>,
  h1: ({ children }) => <h3 className="mb-1 mt-2.5 font-display text-md font-semibold first:mt-0">{children}</h3>,
  h2: ({ children }) => <h3 className="mb-1 mt-2.5 font-display text-md font-semibold first:mt-0">{children}</h3>,
  h3: ({ children }) => <h4 className="mb-1 mt-2 font-semibold first:mt-0">{children}</h4>,
  h4: ({ children }) => <h4 className="mb-1 mt-2 font-semibold first:mt-0">{children}</h4>,
  strong: ({ children }) => <strong className="font-semibold text-ink">{children}</strong>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noreferrer noopener" className="link">
      {children}
    </a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="my-1.5 border-l-2 border-line pl-3 text-ink-2">{children}</blockquote>
  ),
  code: ({ className, children }) =>
    className ? (
      <code className={`${className} font-mono text-sm`}>{children}</code>
    ) : (
      <code className="rounded bg-surface/70 px-1 py-0.5 font-mono text-[0.9em]">{children}</code>
    ),
  pre: ({ children }) => (
    <pre className="my-1.5 overflow-x-auto rounded-control bg-surface/70 p-2.5 text-sm">{children}</pre>
  ),
  table: ({ children }) => (
    <div className="my-1.5 overflow-x-auto">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  th: ({ children }) => <th className="border-b border-line px-2 py-1 text-left font-medium">{children}</th>,
  td: ({ children }) => <td className="border-b border-line/60 px-2 py-1 align-top">{children}</td>,
  hr: () => <hr className="my-2 border-line" />,
}

/**
 * Wiki pages (knowledge base): real heading hierarchy and more air between
 * blocks, for reading a document rather than a chat bubble. Shares the
 * inline styles (links, code, tables) with the chat set.
 */
const pageComponents: Components = {
  ...components,
  p: ({ children }) => <p className="my-3 first:mt-0 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="my-3 list-disc space-y-1 pl-6">{children}</ul>,
  ol: ({ children }) => <ol className="my-3 list-decimal space-y-1 pl-6">{children}</ol>,
  h1: ({ children }) => (
    <h2 className="mb-3 mt-8 font-display text-xl font-semibold tracking-[-0.01em] text-ink first:mt-0">{children}</h2>
  ),
  h2: ({ children }) => (
    <h3 className="mb-2 mt-7 border-b border-line pb-1.5 font-display text-lg font-semibold text-ink first:mt-0">{children}</h3>
  ),
  h3: ({ children }) => <h4 className="mb-2 mt-5 font-display text-md font-semibold text-ink first:mt-0">{children}</h4>,
  h4: ({ children }) => <h5 className="mb-1.5 mt-4 font-semibold text-ink first:mt-0">{children}</h5>,
  h5: ({ children }) => <h6 className="mb-1 mt-3 font-semibold text-ink-2 first:mt-0">{children}</h6>,
  h6: ({ children }) => <h6 className="mb-1 mt-3 font-semibold text-ink-2 first:mt-0">{children}</h6>,
  blockquote: ({ children }) => (
    <blockquote className="my-4 border-l-2 border-line-strong pl-4 text-ink-2">{children}</blockquote>
  ),
  pre: ({ children }) => (
    <pre className="my-4 overflow-x-auto rounded-control bg-sunken p-3.5 text-sm leading-relaxed">{children}</pre>
  ),
  code: ({ className, children }) =>
    className ? (
      <code className={`${className} font-mono text-sm`}>{children}</code>
    ) : (
      <code className="rounded bg-sunken px-1 py-0.5 font-mono text-[0.9em]">{children}</code>
    ),
  table: ({ children }) => (
    <div className="my-4 overflow-x-auto">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  hr: () => <hr className="my-6 border-line" />,
}

/** Where a relative link really goes: the app URL (for the `href`) and the in-place navigation. */
export interface ResolvedRelativeLink {
  href: string
  navigate: () => void
}

export interface MarkdownProps {
  children: string
  /** `chat` (default): compact, body-size headings. `page`: wiki document layout. */
  variant?: 'chat' | 'page'
  /**
   * Resolves a relative link (no scheme, not `#…`), e.g. to another
   * knowledge page. A resolved link gets the real app URL as `href` (so
   * open-in-new-tab works) and navigates in place on a plain click; an
   * unresolvable one is shown as text, so it never leads to a bogus URL.
   */
  resolveRelativeLink?: (href: string) => ResolvedRelativeLink | null
}

function isRelative(href: string | undefined): href is string {
  return !!href && !/^[a-z][a-z0-9+.-]*:/i.test(href) && !href.startsWith('#') && !href.startsWith('//')
}

export function Markdown({ children, variant = 'chat', resolveRelativeLink }: MarkdownProps) {
  const base = variant === 'page' ? pageComponents : components
  const set: Components = resolveRelativeLink
    ? {
        ...base,
        a: ({ href, children: label }) => {
          if (!isRelative(href)) {
            return (
              <a href={href} target="_blank" rel="noreferrer noopener" className="link">
                {label}
              </a>
            )
          }
          const target = resolveRelativeLink(href)
          if (!target) {
            return (
              <span className="text-ink-2 underline decoration-dotted" title={href}>
                {label}
              </span>
            )
          }
          return (
            <a
              href={target.href}
              className="link"
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
                event.preventDefault()
                target.navigate()
              }}
            >
              {label}
            </a>
          )
        },
      }
    : base
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={set}>
      {children}
    </ReactMarkdown>
  )
}
